/**
 * `/admin/users/:adminUserId` — admin-user detail + edit surface
 * (PRD 04 §Identity and access).
 *
 * Every authenticated admin can manage admin accounts (the role-tier
 * model was retired in M15). A 401 sends the admin back to
 * `/admin/sign-in`; a 412 means the actor needs to enrol TOTP and
 * also bounces to sign-in.
 *
 * Three actions live on this page:
 *
 *   - **Edit profile** — full_name / is_active, applied via
 *     `PATCH /admin/users/:adminUserId`. There is no password field —
 *     admin auth is passwordless.
 *   - **Rotate TOTP** (visible only on the actor's own row) — POST
 *     `/totp/enrol` rotates the actor's own secret and surfaces the
 *     new `otpauth_uri` + base32 secret once. Used when the admin
 *     replaces their phone or wants a fresh secret.
 *   - **Reset TOTP** (visible only on a peer's row) — POST
 *     `/totp/reset`, requires a step-up TOTP code from the actor.
 *     Sets the affected admin's `mfa_secret = NULL`; their next
 *     sign-in lands on the forced enrolment screen. Self-reset is
 *     refused server-side (the bootstrap CLI `--reset-totp` flag is
 *     the recovery floor for single-admin deployments).
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiError } from '../../api/client.js';
import type { ApiClient } from '../../api/client.js';
import { ErrorBanner } from '../../components/ErrorBanner.js';
import { TotpEnrolmentDisplay } from '../../components/TotpEnrolmentDisplay.js';
import type { AdminMeResponse, AdminTotpEnrolResponse, AdminUserPublic } from '@isms/shared';

export interface AdminUserDetailPageProps {
  client: ApiClient;
  /** Where to send the admin on 401 / 403 / 412. Defaults to
   *  `/admin/sign-in`. */
  signInPath?: string;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; error: ApiError | string }
  | { kind: 'not_found' }
  | { kind: 'ready'; user: AdminUserPublic; me: AdminMeResponse };

type SaveStatus =
  | { kind: 'idle' }
  | { kind: 'pending' }
  | { kind: 'success'; message: string }
  | { kind: 'error'; error: ApiError | string };

type TotpStatus =
  | { kind: 'idle' }
  | { kind: 'pending' }
  | { kind: 'success'; enrolment: AdminTotpEnrolResponse }
  | { kind: 'error'; error: ApiError | string };

type ResetStatus =
  | { kind: 'idle' }
  | { kind: 'pending' }
  | { kind: 'success'; message: string }
  | { kind: 'error'; message: string; apiError: ApiError | null };

export function AdminUserDetailPage({
  client,
  signInPath = '/admin/sign-in',
}: AdminUserDetailPageProps): JSX.Element {
  const { adminUserId } = useParams<{ adminUserId: string }>();
  const navigate = useNavigate();

  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [profileStatus, setProfileStatus] = useState<SaveStatus>({ kind: 'idle' });
  const [totpStatus, setTotpStatus] = useState<TotpStatus>({ kind: 'idle' });
  const [resetStatus, setResetStatus] = useState<ResetStatus>({ kind: 'idle' });

  const redirectToSignIn = useCallback(() => {
    navigate(signInPath, { replace: true });
  }, [navigate, signInPath]);

  const load = useCallback(async () => {
    if (!adminUserId) return;
    setState({ kind: 'loading' });
    try {
      const [user, me] = await Promise.all([client.getAdminUser(adminUserId), client.getAdminMe()]);
      setState({ kind: 'ready', user, me });
    } catch (err) {
      if (err instanceof ApiError && (err.status === 401 || err.status === 412)) {
        redirectToSignIn();
        return;
      }
      if (err instanceof ApiError && err.status === 404) {
        setState({ kind: 'not_found' });
        return;
      }
      setState({
        kind: 'error',
        error:
          err instanceof ApiError
            ? err
            : "We couldn't load that admin user. Please check your connection and try again.",
      });
    }
  }, [adminUserId, client, redirectToSignIn]);

  useEffect(() => {
    void load();
  }, [load]);

  const saveProfile = useCallback(
    async (patch: { full_name?: string; is_active?: boolean }) => {
      if (!adminUserId) return;
      setProfileStatus({ kind: 'pending' });
      try {
        const updated = await client.adminUpdateUser(adminUserId, patch);
        setState((s) => (s.kind === 'ready' ? { ...s, user: updated } : s));
        setProfileStatus({ kind: 'success', message: 'Changes saved.' });
      } catch (err) {
        if (err instanceof ApiError && (err.status === 401 || err.status === 412)) {
          redirectToSignIn();
          return;
        }
        setProfileStatus({
          kind: 'error',
          error:
            err instanceof ApiError ? err : "We couldn't save those changes. Please try again.",
        });
      }
    },
    [adminUserId, client, redirectToSignIn],
  );

  const rotateOwnTotp = useCallback(async () => {
    if (!adminUserId) return;
    setTotpStatus({ kind: 'pending' });
    try {
      const enrolment = await client.adminEnrolTotp(adminUserId);
      setTotpStatus({ kind: 'success', enrolment });
      await load();
    } catch (err) {
      if (err instanceof ApiError && (err.status === 401 || err.status === 412)) {
        redirectToSignIn();
        return;
      }
      setTotpStatus({
        kind: 'error',
        error:
          err instanceof ApiError ? err : "We couldn't rotate your TOTP secret. Please try again.",
      });
    }
  }, [adminUserId, client, load, redirectToSignIn]);

  const resetPeerTotp = useCallback(
    async (actorTotpCode: string) => {
      if (!adminUserId) return;
      setResetStatus({ kind: 'pending' });
      try {
        await client.adminResetTotp(adminUserId, { actor_totp_code: actorTotpCode });
        setResetStatus({
          kind: 'success',
          message:
            "TOTP cleared. The admin's next sign-in will land on the forced-enrolment screen.",
        });
        await load();
      } catch (err) {
        if (err instanceof ApiError && (err.status === 401 || err.status === 412)) {
          redirectToSignIn();
          return;
        }
        if (err instanceof ApiError && err.code === 'step_up_totp_failed') {
          setResetStatus({
            kind: 'error',
            message: "Your authenticator code didn't verify. Try again with a fresh 6-digit code.",
            apiError: err,
          });
          return;
        }
        setResetStatus({
          kind: 'error',
          message:
            err instanceof ApiError
              ? err.message
              : "We couldn't reset that admin's TOTP. Please try again.",
          apiError: err instanceof ApiError ? err : null,
        });
      }
    },
    [adminUserId, client, load, redirectToSignIn],
  );

  return (
    <main className="admin-user-detail" aria-labelledby="admin-user-detail-heading">
      <nav className="admin-people-breadcrumb" aria-label="Breadcrumb">
        <Link to="/admin/users">← Back to admin users</Link>
      </nav>

      <h1 id="admin-user-detail-heading">Admin user</h1>

      {state.kind === 'loading' ? (
        <p className="admin-people-loading" aria-busy="true">
          Loading admin user…
        </p>
      ) : null}

      {state.kind === 'not_found' ? (
        <div className="admin-people-error" role="alert">
          <p>We couldn't find that admin. They may have been removed.</p>
          <Link to="/admin/users">Back to admin users</Link>
        </div>
      ) : null}

      {state.kind === 'error' ? (
        <div className="admin-people-error">
          <ErrorBanner error={state.error} />
          <button type="button" onClick={() => void load()}>
            Retry
          </button>
        </div>
      ) : null}

      {state.kind === 'ready' ? (
        <>
          <UserHeader user={state.user} />
          <ProfileForm
            user={state.user}
            status={profileStatus}
            onSubmit={(patch) => void saveProfile(patch)}
          />
          {String(state.user.admin_user_id) === String(state.me.admin_user_id) ? (
            <SelfTotpPanel
              user={state.user}
              status={totpStatus}
              onRotate={() => void rotateOwnTotp()}
            />
          ) : (
            <PeerTotpResetPanel
              user={state.user}
              status={resetStatus}
              onReset={(code) => void resetPeerTotp(code)}
            />
          )}
        </>
      ) : null}
    </main>
  );
}

interface UserHeaderProps {
  user: AdminUserPublic;
}

function UserHeader({ user }: UserHeaderProps): JSX.Element {
  return (
    <section className="admin-user-detail-header" aria-label="Admin summary">
      <div className="admin-user-detail-identity">
        <span className="admin-user-detail-name">{user.full_name}</span>
        <span className="admin-user-detail-email">{user.email}</span>
      </div>
      <div className="admin-user-detail-badges">
        <span
          className={`admin-people-status ${
            user.is_active ? 'admin-people-status-active' : 'admin-people-status-inactive'
          }`}
        >
          {user.is_active ? 'Active' : 'Deactivated'}
        </span>
        <span
          className={`admin-users-mfa-chip ${
            user.mfa_enrolled ? 'admin-users-mfa-enrolled' : 'admin-users-mfa-missing'
          }`}
        >
          {user.mfa_enrolled ? 'TOTP set' : 'TOTP missing'}
        </span>
      </div>
      <dl className="admin-user-detail-meta">
        <dt>Created</dt>
        <dd>{formatTimestamp(user.created_at)}</dd>
        <dt>Last signed in</dt>
        <dd>{user.last_login_at ? formatTimestamp(user.last_login_at) : 'Never'}</dd>
      </dl>
    </section>
  );
}

interface ProfileFormProps {
  user: AdminUserPublic;
  status: SaveStatus;
  onSubmit: (patch: { full_name?: string; is_active?: boolean }) => void;
}

function ProfileForm({ user, status, onSubmit }: ProfileFormProps): JSX.Element {
  const [fullName, setFullName] = useState(user.full_name);
  const [isActive, setIsActive] = useState(user.is_active);

  useEffect(() => {
    setFullName(user.full_name);
    setIsActive(user.is_active);
  }, [user.full_name, user.is_active]);

  const pending = status.kind === 'pending';
  const dirty = fullName.trim() !== user.full_name || isActive !== user.is_active;

  function handleSubmit(event: React.FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (!dirty || pending) return;
    const trimmed = fullName.trim();
    if (!trimmed) return;
    const patch: { full_name?: string; is_active?: boolean } = {};
    if (trimmed !== user.full_name) patch.full_name = trimmed;
    if (isActive !== user.is_active) patch.is_active = isActive;
    onSubmit(patch);
  }

  return (
    <section className="admin-user-detail-section" aria-labelledby="admin-user-profile-heading">
      <h2 id="admin-user-profile-heading">Profile</h2>
      <form onSubmit={handleSubmit} noValidate className="admin-users-create-form">
        <div className="admin-users-form-row">
          <label htmlFor="admin-user-full-name">Full name</label>
          <input
            id="admin-user-full-name"
            type="text"
            autoComplete="off"
            required
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            disabled={pending}
          />
        </div>
        <div className="admin-users-form-row admin-users-form-row-checkbox">
          <label htmlFor="admin-user-is-active">
            <input
              id="admin-user-is-active"
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              disabled={pending}
            />
            Account is active — sign-in allowed
          </label>
          <p className="admin-users-form-hint">
            Deactivate an admin to refuse further sign-ins and reject any live session on the next
            middleware check.
          </p>
        </div>
        <div className="admin-users-create-actions">
          <button
            type="submit"
            className="admin-exports-primary-button"
            disabled={pending || !dirty}
          >
            {pending ? 'Saving…' : 'Save changes'}
          </button>
        </div>
        {status.kind === 'success' ? (
          <p className="admin-exports-action-success" role="status">
            {status.message}
          </p>
        ) : null}
        {status.kind === 'error' ? <ErrorBanner error={status.error} /> : null}
      </form>
    </section>
  );
}

interface SelfTotpPanelProps {
  user: AdminUserPublic;
  status: TotpStatus;
  onRotate: () => void;
}

function SelfTotpPanel({ user, status, onRotate }: SelfTotpPanelProps): JSX.Element {
  const pending = status.kind === 'pending';
  return (
    <section className="admin-user-detail-section" aria-labelledby="admin-user-totp-heading">
      <h2 id="admin-user-totp-heading">Rotate your authenticator app (TOTP)</h2>
      <p className="admin-users-form-hint">
        Rotate when you replace your phone or want a fresh secret. The old authenticator entry stops
        working immediately. {user.mfa_enrolled ? null : 'You currently have no TOTP enrolled.'}
      </p>
      <button
        type="button"
        className="admin-exports-primary-button"
        onClick={onRotate}
        disabled={pending}
      >
        {pending ? 'Generating…' : 'Rotate TOTP'}
      </button>
      {status.kind === 'success' ? <TotpSecretDisplay enrolment={status.enrolment} /> : null}
      {status.kind === 'error' ? <ErrorBanner error={status.error} /> : null}
    </section>
  );
}

interface PeerTotpResetPanelProps {
  user: AdminUserPublic;
  status: ResetStatus;
  onReset: (actorTotpCode: string) => void;
}

function PeerTotpResetPanel({ user, status, onReset }: PeerTotpResetPanelProps): JSX.Element {
  const [stepUp, setStepUp] = useState('');
  const pending = status.kind === 'pending';

  function handleSubmit(event: React.FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (pending) return;
    if (!/^\d{6}$/.test(stepUp.trim())) return;
    onReset(stepUp.trim());
    setStepUp('');
  }

  return (
    <section className="admin-user-detail-section" aria-labelledby="admin-user-totp-heading">
      <h2 id="admin-user-totp-heading">Reset this admin's TOTP</h2>
      <p className="admin-users-form-hint">
        Use this when {user.full_name || 'the admin'} has lost their authenticator device. Their
        next sign-in lands on the forced-enrolment screen, where they pair a new device. To protect
        against a stolen session, you must enter a fresh code from <em>your own</em> authenticator
        app to confirm.
      </p>
      <form onSubmit={handleSubmit} noValidate className="admin-users-create-form">
        <div className="admin-users-form-row">
          <label htmlFor="admin-user-step-up-totp">Your 6-digit authenticator code (step-up)</label>
          <input
            id="admin-user-step-up-totp"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]{6}"
            maxLength={6}
            value={stepUp}
            onChange={(e) => setStepUp(e.target.value.replace(/\D/g, ''))}
            disabled={pending}
            required
          />
        </div>
        <div className="admin-users-create-actions">
          <button
            type="submit"
            className="admin-exports-primary-button"
            disabled={pending || stepUp.length !== 6}
          >
            {pending ? 'Resetting…' : 'Reset TOTP'}
          </button>
        </div>
        {status.kind === 'success' ? (
          <p className="admin-exports-action-success" role="status">
            {status.message}
          </p>
        ) : null}
        {status.kind === 'error' ? (
          <ErrorBanner error={status.apiError ?? status.message} message={status.message} />
        ) : null}
      </form>
    </section>
  );
}

interface TotpSecretDisplayProps {
  enrolment: AdminTotpEnrolResponse;
}

function TotpSecretDisplay({ enrolment }: TotpSecretDisplayProps): JSX.Element {
  return (
    <TotpEnrolmentDisplay
      otpauthUri={enrolment.otpauth_uri}
      secretBase32={enrolment.secret_base32}
      idPrefix="admin-user-totp"
      warning={
        <>
          <strong>Save this secret now</strong> — it's shown exactly once. Hand it off to the admin
          so they can scan it into an authenticator app.
        </>
      }
    />
  );
}

function formatTimestamp(v: string | Date): string {
  const s = typeof v === 'string' ? v : v.toISOString();
  return s.replace('T', ' ').replace(/\..*$/, '').slice(0, 16);
}
